import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Phone, MessageCircle, Clock3, ChevronRight, ArrowRight, MapPin, Navigation,
} from "lucide-react";
import { SplitLines, FadeUp, Eyebrow } from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import { CtaBanner } from "../components/CtaBanner";
import { SERVICES } from "../data/services";
import { SUB_AREAS, getSubArea, nearbySubAreas, districtTitle } from "../data/subAreas";
import { PHONE_TEL, PHONE_DISPLAY, waLink } from "../data/site";

export default function SubAreaHub() {
  const { areaSlug } = useParams();
  const area = getSubArea(areaSlug);
  const d = area ? districtTitle(area.district) : "";
  const waMsg = `Hi Motion Tyres — I need a tyre service in ${d}. Here's my location:`;

  useEffect(() => {
    if (area) document.title = `Mobile Tyres in ${d} | 24/7 Fitting & Repair | Motion Tyres`;
    return () => { document.title = "Motion Tyres | 24/7 Mobile Tyre Fitting — We Come To You"; };
  }, [area, d]);

  if (!area) {
    return (
      <div className="pt-40 pb-24 max-w-7xl mx-auto px-5 sm:px-8" data-testid="subarea-not-found">
        <h1 className="font-display font-extrabold uppercase text-5xl">Area not found</h1>
        <Link to="/" className="text-[#FF5500] underline mt-4 inline-block">Back to home</Link>
      </div>
    );
  }

  const town = area.town;
  const nearby = nearbySubAreas(area);

  return (
    <div data-testid={`subarea-hub-${area.slug}`}>
      {/* HERO */}
      <section className="relative pt-32 sm:pt-40 pb-14 overflow-hidden" data-testid="subarea-hero">
        <div
          className="absolute -top-24 right-0 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,85,0,0.16) 0%, transparent 70%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <motion.nav
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="flex flex-wrap items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-6"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/areas/${town.slug}`} className="hover:text-white transition-colors">{town.name}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#FF5500]">{area.district}</span>
          </motion.nav>

          <Eyebrow>Sub-area — 24/7 mobile cover</Eyebrow>
          <h1 className="font-display font-extrabold uppercase leading-[0.92] text-4xl sm:text-6xl lg:text-7xl mt-4">
            <SplitLines delay={0.25} accentLast lines={["MOBILE TYRES", `IN ${d.toUpperCase()}.`]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-4 text-zinc-400 text-lg max-w-2xl"
          >
            Part of our {town.name} operation — {town.postcodes}, main routes {town.routes}.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <span className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.15em] text-black bg-[#FF5500] px-4 py-2.5 rounded-full" data-testid="subarea-eta-badge">
              <Clock3 className="w-4 h-4" /> {town.eta} typical response
            </span>
            <a
              href={PHONE_TEL}
              data-testid="subarea-call-button"
              className="btn-glow flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF5500] hover:bg-[#FF6A1A] text-black font-telemetry text-sm font-semibold uppercase tracking-[0.12em] transition-colors"
            >
              <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
            </a>
            <a
              href={waLink(waMsg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="subarea-whatsapp-button"
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#16A34A] hover:bg-[#1AB055] text-black font-telemetry text-sm font-semibold uppercase tracking-[0.12em] transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp location
            </a>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="border-y border-[#27272A] bg-[#0E0E10]" data-testid="subarea-intro-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <FadeUp className="lg:col-span-8">
            <p className="text-zinc-300 text-base sm:text-lg leading-relaxed">
              Motion Tyres covers <span className="text-white font-semibold">{area.district}</span> around
              the clock as part of our {town.name} operation — mobile tyre fitting, puncture repair, wheel
              alignment and seasonal swaps, all from a fully equipped van at your location. Pick a service
              below for a fixed quote, or call the dispatch line and a technician is typically with you in{" "}
              {town.eta}, any hour of the day.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-4">
            <div className="bg-[#131316] border border-[#27272A] p-6">
              <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-[#FF5500] mb-4 flex items-center gap-2">
                <Navigation className="w-4 h-4" /> Area briefing
              </p>
              <dl className="space-y-3 text-sm">
                {[
                  ["District", area.district],
                  ["Part of", town.name],
                  ["Response", town.eta],
                  ["Routes", town.routes],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-[#27272A] pb-3 last:pb-0 last:border-0">
                    <dt className="font-telemetry text-[10px] uppercase tracking-[0.2em] text-zinc-500 pt-0.5">{k}</dt>
                    <dd className="font-display font-bold uppercase text-base text-white text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* SERVICES IN THIS SUB-AREA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="subarea-services-section">
        <FadeUp>
          <Eyebrow>Available now in {d}</Eyebrow>
          <h2 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-3 mb-12">
            Pick your service.
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <FadeUp key={s.slug} delay={i * 0.05}>
              <Link
                to={`/services/${s.slug}/in/${area.slug}`}
                data-testid={`subarea-service-link-${s.slug}`}
                className="group flex items-center justify-between gap-4 bg-[#131316] border border-[#27272A] hover:border-[#FF5500]/60 transition-colors px-6 py-5 card-line"
              >
                <div>
                  <h3 className="font-display font-extrabold uppercase text-xl text-white">
                    {s.shortName} in {d}
                  </h3>
                  <p className="text-xs text-zinc-500 mt-1 font-telemetry uppercase tracking-[0.15em]">
                    {town.eta} · {town.name}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#FF5500] transition-transform duration-300 group-hover:translate-x-1 shrink-0" />
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* NEARBY SUB-AREAS */}
      <section className="border-y border-[#27272A] bg-[#0E0E10]" data-testid="subarea-nearby-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
          <FadeUp>
            <p className="font-telemetry text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-6 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#FF5500]" /> Nearby areas in {town.name}
            </p>
            <div className="flex flex-wrap gap-3">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to={`/in/${n.slug}`}
                  data-testid={`nearby-chip-${n.slug}`}
                  className="px-5 py-3 rounded-full border border-[#27272A] hover:border-[#FF5500]/60 transition-colors text-sm text-zinc-300 hover:text-white"
                >
                  {n.district}
                </Link>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <CtaBanner
        title={`STRANDED IN ${d.toUpperCase()}?`}
        sub={`A Motion Tyres van covers ${area.district} 24/7 as part of our ${town.name} operation — typical response ${town.eta}.`}
      />

      {/* BOOKING */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="subarea-booking-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <FadeUp>
            <Eyebrow>Book in {d}</Eyebrow>
            <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl mt-3 leading-[0.95]">
              A van, outside
              <br />
              <span className="text-[#FF5500]">your door today.</span>
            </h2>
            <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
              Tap the live-location button and we'll pinpoint your spot in {area.district} — your
              enquiry then lands straight on our dispatch WhatsApp with the map pin attached.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <ContactForm />
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
